import React, { useCallback, useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { startCheckout } from "../../../lib/checkout";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import { trackEvent } from "../../../lib/track";
import { getAttributionV2 } from "../../../lib/utm";

/**
 * ReservationModal — collects name+email, then redirects the visitor to
 * the real Stripe Checkout for the selected product. The visitor will
 * return to /checkout/success or /checkout/cancel.
 */
const initialForm = { name: "", email: "" };

const ReservationModal = ({
  open,
  onClose,
  product,
  eyebrow,
  title,
  description,
  submitLabel = "Continue to Checkout",
  busyLabel = "Preparing checkout…",
  testIdPrefix = "modal",
}) => {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);

  const close = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEscapeKey(close, open && !busy);

  // Body-scroll-lock + signal to the sticky mobile CTA to hide.
  useEffect(() => {
    if (!open) return undefined;
    // Phase 2: fire `checkout_intent` exactly once per modal open. This
    // is the upstream funnel marker (button click / modal opened) BEFORE
    // the visitor has entered their email or interacted with Stripe.
    const v2 = getAttributionV2();
    trackEvent("checkout_intent", {
      product_type: product,
      src: v2.src,
      campaign: v2.campaign,
    });
    document.documentElement.classList.add("am-modal-open");
    const y = window.scrollY;
    const prev = document.body.style.cssText;
    document.body.style.position = "fixed";
    document.body.style.top = `-${y}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    return () => {
      document.documentElement.classList.remove("am-modal-open");
      document.body.style.cssText = prev;
      window.scrollTo(0, y);
    };
  }, [open]);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!form.name || !form.email) {
        toast.error("Please enter your name and email.");
        return;
      }
      try {
        setBusy(true);
        await startCheckout(product, form);
        // Redirect happens inside startCheckout — keep modal in busy state.
      } catch {
        setBusy(false);
        toast.error("Something paused the checkout. Please try again.");
      }
    },
    [form, product]
  );

  if (!open) return null;

  return (
    <div
      data-testid={`${testIdPrefix}-modal`}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md px-6"
      onClick={() => !busy && onClose?.()}
    >
      <div className="glass-card max-w-md w-full p-10" onClick={(e) => e.stopPropagation()}>
        <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-4">
          {eyebrow}
        </p>
        <h3 className="font-serif text-3xl text-themed leading-tight">{title}</h3>
        <p className="text-sm text-themed-muted mt-3 mb-8">{description}</p>
        <form onSubmit={submit} className="space-y-6">
          <input
            data-testid={`${testIdPrefix}-modal-name`}
            type="text"
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input-bare"
            required
            disabled={busy}
          />
          <input
            data-testid={`${testIdPrefix}-modal-email`}
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="input-bare"
            required
            disabled={busy}
          />
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={busy}
              data-testid={`${testIdPrefix}-modal-submit`}
              className="btn-gold disabled:opacity-60 inline-flex items-center gap-2"
            >
              {busy && <Sparkles size={14} className="animate-pulse" />}
              {busy ? busyLabel : submitLabel}
            </button>
            <button
              type="button"
              data-testid={`${testIdPrefix}-modal-close`}
              onClick={() => onClose?.()}
              disabled={busy}
              className="text-xs tracking-[0.2em] uppercase text-themed-muted hover:text-themed disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
          <p className="text-[10px] tracking-[0.18em] uppercase text-themed-muted pt-3">
            Secure payment by Stripe
          </p>
        </form>
      </div>
    </div>
  );
};

export default ReservationModal;
